import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth, formatApiErrorDetail } from "@/lib/AdminAuthContext";
import { toast } from "sonner";
import { Lock, ArrowLeft, EnvelopeSimple, Key } from "@phosphor-icons/react";

const LOGO_IMG =
  "/assets/hero-logo.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, checking, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#080412] text-white flex items-center justify-center font-sora text-sm uppercase tracking-[0.28em] text-white/60">
        Checking session…
      </div>
    );
  }
  if (user) return <Navigate to="/admin/orders" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate("/admin/orders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#080412] text-white flex items-center justify-center px-5 py-10 relative overflow-hidden"
      data-testid="admin-login-page"
    >
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{
        background:
          "radial-gradient(50% 40% at 20% 20%, rgba(123,44,255,0.28), transparent 60%), radial-gradient(45% 40% at 85% 85%, rgba(255,44,140,0.20), transparent 65%)",
      }} />
      <div aria-hidden className="grain absolute inset-0 -z-10 opacity-30" />

      <div className="w-full max-w-md">
        <Link
          to="/"
          className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.28em] text-white/60 flex items-center gap-2 mb-8 hover:text-brandpink transition-colors"
          data-testid="admin-login-back-link"
        >
          <ArrowLeft size={14} weight="bold" /> Back to site
        </Link>

        <div className="rounded-3xl hr-glass hr-neon-pink p-8 md:p-10 relative overflow-hidden">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={LOGO_IMG}
              alt="House Rules"
              className="h-12 w-auto rounded-lg drop-shadow-[0_0_18px_rgba(167,66,255,0.5)]"
            />
            <div>
              <div className="font-sora font-semibold text-[10px] uppercase tracking-[0.32em] text-brandpink">
                Admin Console
              </div>
              <div className="font-sora text-[10px] uppercase tracking-[0.28em] text-white/50">Secure area</div>
            </div>
          </div>

          <h1
            className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-[-0.01em] mb-8 break-words"
            data-testid="admin-login-headline"
          >
            Sign <span className="italic font-editorial text-brandpink hr-text-glow-pink">In.</span>
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60 mb-1.5 block">
                Email
              </label>
              <div className="relative">
                <EnvelopeSimple
                  size={16}
                  weight="duotone"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brandpink"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 font-sans text-base bg-white/[0.04] border border-white/15 rounded-lg focus:outline-none focus:border-brandpink text-white placeholder-white/40"
                  placeholder="you@houserules.com"
                  data-testid="admin-login-email"
                  required
                />
              </div>
            </div>
            <div>
              <label className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60 mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <Key
                  size={16}
                  weight="duotone"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brandpink"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 font-sans text-base bg-white/[0.04] border border-white/15 rounded-lg focus:outline-none focus:border-brandpink text-white placeholder-white/40"
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="hr-cta w-full h-12 rounded-full text-white font-sora font-semibold text-[11px] uppercase tracking-[0.22em] flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              <Lock size={14} weight="fill" /> {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 font-sora text-[10px] uppercase tracking-[0.24em] text-white/40 text-center">
            Session is scoped to this device only.
          </p>
        </div>
      </div>
    </div>
  );
}
